import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Chip from '@zenbusiness/zui/Chip';
import DialogActions from '@zenbusiness/zui/DialogActions';
import DialogContent from '@zenbusiness/zui/DialogContent';
import Grid from '@zenbusiness/zui/Grid';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';

import { useChangeRequestContext, useSetChangeRequest } from '../hooks/useChangeRequestContext';
import getSharedAsset from '../utils/sharedAssetMap';

export default function ChangeBillingIntervalAnnualComparison({
  subscription,
  serviceDisplayName,
  products,
  billingChangeSavings
}) {
  const [itemSelected, setItemSelected] = useState(null);
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { changeFlow } = useChangeRequestContext();
  const { setChangeFlow } = useSetChangeRequest();
  const { modalFlow } = changeFlow;
  const { changeBillingIntervalAnnual } = modalFlow;

  useEffect(() => {
    if (itemSelected !== null) return;
    if (!products?.length) return;
    const currentProduct = products.find((p) => p.pricing?.interval === subscription?.period);
    if (currentProduct) {
      setItemSelected(currentProduct.pricing.interval);
    }
  }, [products, subscription?.period, itemSelected]);

  const assetUrl = getSharedAsset(subscription?.service || '');
  const submitDisabled = itemSelected === subscription?.period;

  const today = new Date();
  const effectiveDate = [
    (today.getMonth() + 1).toString().padStart(2, '0'),
    today.getDate().toString().padStart(2, '0'),
    today.getFullYear()
  ].join('/');

  const handleContinue = () => {
    if (submitDisabled) return;
    setChangeFlow((prevState) => ({
      ...prevState,
      orderBillingItemUuid: subscription?.orderBillingItemUuid,
      interval: itemSelected,
      modalFlow: {
        ...modalFlow,
        changeBillingIntervalAnnual: {
          ...changeBillingIntervalAnnual,
          stepOne: false,
          stepTwo: true
        }
      }
    }));
  };

  return (
    <>
      <DialogContent>
        <Box mb={4} textAlign="center">
          <Text size="xl" weight="bold" align="center" sx={{ mb: 1 }}>
            <FormattedMessage id="dashboard.subscriptions.billingChange.comparisonTitle" />
          </Text>
          <Text size="md" color="secondary" align="center">
            <FormattedMessage id="dashboard.subscriptions.billingChange.comparisonSubtitle" />
          </Text>
        </Box>

        <Stack alignItems="center" sx={{ mb: 3 }}>
          <Box
            component="img"
            src={assetUrl}
            alt={serviceDisplayName}
            sx={{ width: 96, height: 96, mb: 1 }}
          />
          <Text size="lg" weight="bold" align="center" sx={{ textTransform: 'capitalize' }}>
            {serviceDisplayName}
          </Text>
        </Stack>

        {products?.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {products.map(({ pricing: { uuid, interval, amount } }) => (
              <Grid item xs={12} sm={6} key={`product-pricing-${uuid}`}>
                <Box
                  data-testid="set-item-selected"
                  onClick={() => setItemSelected(interval)}
                  sx={{
                    border: '1px solid',
                    borderColor: itemSelected === interval
                      ? theme.palette.text.primary
                      : theme.palette.divider,
                    borderRadius: '1rem',
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: itemSelected === interval
                      ? theme.palette.action.selected
                      : theme.palette.background.paper,
                    color: 'inherit'
                  }}
                >
                  <Text
                    weight="bold"
                    align="center"
                    sx={{
                      textTransform: 'capitalize',
                      mb: 1,
                      color: 'inherit'
                    }}
                  >
                    {interval === 'annual'
                      ? <FormattedMessage id="dashboard.subscriptions.billingChange.annually" />
                      : interval}
                  </Text>
                  <Text align="center" sx={{ fontSize: 16, color: 'inherit' }}>
                    {`$${amount} / ${interval === 'annual'
                      ? formatMessage({ id: 'dashboard.subscriptions.price.periodYear' }).toLowerCase()
                      : formatMessage({ id: 'dashboard.subscriptions.price.periodMonth' }).toLowerCase()}`}
                  </Text>
                  {interval === subscription?.period && (
                    <Box mt={1}>
                      <Chip
                        label={formatMessage({ id: 'dashboard.subscriptions.billingChange.currentPlan' })}
                        size="small"
                        color="default"
                        sx={{ mb: 0.5 }}
                      />
                      <Text
                        align="center"
                        sx={{ fontSize: 12, display: 'block', color: 'inherit' }}
                      >
                        {formatMessage({ id: 'dashboard.subscriptions.billingChange.renews' }, { date: subscription.termEndDate })}
                      </Text>
                    </Box>
                  )}
                  {interval !== subscription?.period && interval === 'annual' && (
                    <Box mt={1}>
                      <Chip
                        label={formatMessage({ id: 'dashboard.subscriptions.menu.savePct' }, { pct: billingChangeSavings })}
                        size="small"
                        color="success"
                        sx={{ mb: 0.5 }}
                      />
                      <Text
                        align="center"
                        sx={{ fontSize: 12, display: 'block', color: 'inherit' }}
                      >
                        {formatMessage({ id: 'dashboard.subscriptions.billingChange.effective' }, { date: effectiveDate })}
                      </Text>
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 3 }}>
        <Button
          variant="contained"
          disabled={submitDisabled}
          onClick={handleContinue}
          clickstreamProps={{ elementName: 'billing_change_continue_to_review' }}
          data-testid="submit-product-selection"
        >
          <FormattedMessage id="dashboard.subscriptions.billingChange.continueToReview" />
        </Button>
      </DialogActions>
    </>
  );
}

ChangeBillingIntervalAnnualComparison.propTypes = {
  subscription: PropTypes.shape({
    orderBillingItemUuid: PropTypes.string,
    service: PropTypes.string,
    period: PropTypes.string,
    termEndDate: PropTypes.string
  }).isRequired,
  serviceDisplayName: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.shape({
    pricing: PropTypes.shape({
      uuid: PropTypes.string,
      interval: PropTypes.string,
      amount: PropTypes.number
    })
  })),
  billingChangeSavings: PropTypes.number
};
